import { useEffect, useMemo, useState } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { json } from "@codemirror/lang-json";
import { placeholder } from "@codemirror/view";
import { vscodeDark } from "@uiw/codemirror-theme-vscode";
import { useTranslation } from "react-i18next";
import { Toast } from "../../components/Toast";

type ValidationState =
  | { status: "empty" }
  | { status: "valid" }
  | { status: "invalid"; line?: number; column?: number };

type ParseResult =
  | { data: unknown }
  | { error: { line?: number; column?: number } };

const parseJson = (value: string): ParseResult => {
  try {
    return { data: JSON.parse(value) };
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    const lineColumnMatch = message.match(/line\s+(\d+)\s+column\s+(\d+)/i);
    if (lineColumnMatch) {
      return {
        error: {
          line: Number(lineColumnMatch[1]),
          column: Number(lineColumnMatch[2]),
        },
      };
    }

    const positionMatch = message.match(/position\s+(\d+)/i);
    if (positionMatch) {
      const position = Number(positionMatch[1]);
      if (!Number.isNaN(position)) {
        const lines = value.slice(0, position).split("\n");
        return {
          error: {
            line: lines.length,
            column: lines[lines.length - 1]?.length + 1,
          },
        };
      }
    }

    return { error: {} };
  }
};

const INDENT = "  ";

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (!value || typeof value !== "object") {
    return false;
  }
  return Object.prototype.toString.call(value) === "[object Object]";
};

const looksLikeNumber = (value: string) => {
  if (/^-?0\d+/.test(value)) {
    return true;
  }
  return /^-?(?:0|[1-9]\d*)(?:\.\d+)?(?:[eE][+-]?\d+)?$/.test(value);
};

const needsQuoting = (value: string) => {
  if (value === "") {
    return true;
  }
  if (value.trim() !== value) {
    return true;
  }
  if (value === "true" || value === "false" || value === "null") {
    return true;
  }
  if (value.startsWith("-")) {
    return true;
  }
  if (looksLikeNumber(value)) {
    return true;
  }
  return /[:"\\\[\]\{\}\n\r\t,]/.test(value);
};

const escapeString = (value: string) =>
  value
    .replace(/\\/g, "\\\\")
    .replace(/"/g, '\\"')
    .replace(/\n/g, "\\n")
    .replace(/\r/g, "\\r")
    .replace(/\t/g, "\\t");

const formatPrimitive = (value: unknown) => {
  if (value === null) {
    return "null";
  }
  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }
  if (typeof value === "number") {
    return Number.isFinite(value) ? String(value) : "null";
  }
  if (typeof value === "string") {
    return needsQuoting(value) ? `"${escapeString(value)}"` : value;
  }
  return "null";
};

const isPrimitive = (value: unknown) =>
  value === null || ["string", "number", "boolean"].includes(typeof value);

const isTabularArray = (value: unknown[]) => {
  if (value.length === 0) {
    return false;
  }
  if (!value.every((item) => isPlainObject(item))) {
    return false;
  }
  const keys = Object.keys(value[0] as Record<string, unknown>);
  if (keys.length === 0) {
    return false;
  }
  return value.every((item) => {
    const record = item as Record<string, unknown>;
    const itemKeys = Object.keys(record);
    if (itemKeys.length !== keys.length) {
      return false;
    }
    return keys.every((key) => key in record && isPrimitive(record[key]));
  });
};

const formatArray = (
  array: unknown[],
  indentLevel: number,
  key?: string,
): string[] => {
  const indent = INDENT.repeat(indentLevel);
  const prefix = key
    ? `${indent}${key}[${array.length}]`
    : `${indent}[${array.length}]`;

  if (array.length === 0) {
    return [`${prefix}:`];
  }

  if (array.every(isPrimitive)) {
    const values = array.map((item) => formatPrimitive(item)).join(",");
    return [`${prefix}:${values}`];
  }

  if (isTabularArray(array)) {
    const headerKeys = Object.keys(array[0] as Record<string, unknown>);
    const header = `${prefix}{${headerKeys.join(",")}}:`;
    const rows = array.map((item) => {
      const record = item as Record<string, unknown>;
      return headerKeys
        .map((field) => formatPrimitive(record[field]))
        .join(",");
    });
    return [
      header,
      ...rows.map((row) => `${INDENT.repeat(indentLevel + 1)}${row}`),
    ];
  }

  const lines = [`${prefix}:`];
  array.forEach((item) => {
    const itemIndent = INDENT.repeat(indentLevel + 1);
    if (isPrimitive(item)) {
      lines.push(`${itemIndent}- ${formatPrimitive(item)}`);
      return;
    }
    lines.push(`${itemIndent}-`);
    lines.push(...formatToon(item, indentLevel + 2));
  });
  return lines;
};

const formatObject = (value: Record<string, unknown>, indentLevel: number) => {
  const lines: string[] = [];
  const indent = INDENT.repeat(indentLevel);
  Object.keys(value).forEach((key) => {
    const entry = value[key];
    if (Array.isArray(entry)) {
      lines.push(...formatArray(entry, indentLevel, key));
      return;
    }
    if (isPlainObject(entry)) {
      const nestedKeys = Object.keys(entry);
      if (nestedKeys.length === 0) {
        lines.push(`${indent}${key}:`);
        return;
      }
      lines.push(`${indent}${key}:`);
      lines.push(...formatObject(entry, indentLevel + 1));
      return;
    }
    lines.push(`${indent}${key}:${formatPrimitive(entry)}`);
  });
  return lines;
};

const formatToon = (value: unknown, indentLevel = 0): string[] => {
  if (Array.isArray(value)) {
    return formatArray(value, indentLevel);
  }
  if (isPlainObject(value)) {
    const keys = Object.keys(value);
    if (keys.length === 0) {
      return [];
    }
    return formatObject(value, indentLevel);
  }
  return [INDENT.repeat(indentLevel) + formatPrimitive(value)];
};

export function JsonToToon() {
  const { t } = useTranslation();
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [validation, setValidation] = useState<ValidationState>({
    status: "empty",
  });
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [reduction, setReduction] = useState<number | null>(null);

  useEffect(() => {
    const trimmed = input.trim();
    if (!trimmed) {
      setValidation({ status: "empty" });
      return;
    }

    const result = parseJson(input);
    if ("error" in result) {
      setValidation({ status: "invalid", ...result.error });
      return;
    }

    setValidation({ status: "valid" });
  }, [input]);

  const convertToToon = () => {
    const trimmed = input.trim();
    if (!trimmed) {
      setOutput("");
      setReduction(null);
      setValidation({ status: "empty" });
      return;
    }

    const result = parseJson(input);
    if ("error" in result) {
      setValidation({ status: "invalid", ...result.error });
      return;
    }

    const toon = formatToon(result.data).join("\n");
    setOutput(toon);
    setValidation({ status: "valid" });
    if (trimmed.length > 0) {
      const percent = Math.max(
        0,
        Math.min(100, Math.round((1 - toon.length / trimmed.length) * 100)),
      );
      setReduction(percent);
    } else {
      setReduction(null);
    }
  };

  const copyOutput = async () => {
    if (!output) {
      return;
    }

    try {
      await navigator.clipboard.writeText(output);
      setToastMessage(t("jsonToToon.copied"));
    } catch {
      setToastMessage(t("jsonToToon.copyFailed"));
    }
  };

  const clearAll = () => {
    setInput("");
    setOutput("");
    setReduction(null);
    setValidation({ status: "empty" });
  };

  const validationMessage = useMemo(() => {
    if (validation.status === "empty") {
      return null;
    }
    if (validation.status === "valid") {
      return (
        <p className="jf-status jf-status-valid">{t("jsonToToon.valid")}</p>
      );
    }

    const hasPosition = validation.line && validation.column;
    return (
      <p className="jf-status jf-status-error">
        {t("jsonToToon.invalid")}
        {hasPosition && (
          <span className="jf-status-position">
            {t("jsonToToon.errorPosition", {
              line: validation.line,
              column: validation.column,
            })}
          </span>
        )}
      </p>
    );
  }, [t, validation]);

  return (
    <div className="json-formatter">
      <div className="jf-grid">
        <div className="jf-card">
          <label className="jf-label">{t("jsonToToon.inputLabel")}</label>
          <CodeMirror
            value={input}
            theme={vscodeDark}
            basicSetup={{
              lineNumbers: true,
              highlightActiveLine: true,
              highlightActiveLineGutter: true,
              foldGutter: true,
            }}
            extensions={[json(), placeholder(t("jsonToToon.inputPlaceholder"))]}
            className="jt-editor"
            onChange={(value) => setInput(value)}
            aria-label={t("jsonToToon.inputLabel")}
          />
          <div className="jf-actions">
            <button
              type="button"
              className="jf-primary-btn"
              onClick={convertToToon}
              disabled={!input.trim()}
            >
              {t("jsonToToon.convert")}
            </button>
          </div>
          {validationMessage}
        </div>

        <div className="jf-card">
          <label className="jf-label">{t("jsonToToon.outputLabel")}</label>
          <CodeMirror
            value={output}
            theme={vscodeDark}
            basicSetup={{
              lineNumbers: true,
              highlightActiveLine: false,
              highlightActiveLineGutter: false,
              foldGutter: false,
            }}
            extensions={[placeholder(t("jsonToToon.outputPlaceholder"))]}
            className="jt-editor jt-editor-output"
            editable={false}
            aria-label={t("jsonToToon.outputLabel")}
          />
          <div className="jf-actions">
            <button
              type="button"
              className="jf-secondary-btn"
              onClick={copyOutput}
              disabled={!output}
            >
              {t("jsonToToon.copy")}
            </button>
            <button
              type="button"
              className="jf-ghost-btn"
              onClick={clearAll}
              disabled={!input && !output}
            >
              {t("jsonToToon.clear")}
            </button>
          </div>
          {reduction !== null && (
            <p className="jf-status">
              {t("jsonToToon.tokenReduction", { percent: reduction })}
            </p>
          )}
        </div>
      </div>

      {toastMessage && (
        <Toast message={toastMessage} onClose={() => setToastMessage(null)} />
      )}
    </div>
  );
}
